
import { CharacterCondition, CharacterGift, CharacterItem, CharacterMagicItemKeyword } from "@dndModels/character_feature_source";
import { DataType } from "@dndModels/Types";
import { PlayerObject } from "@models";

/**
 * This is an object that allows for DMs and players to send items or conditions to players in a campaign.
 */
export class Receivable extends PlayerObject {
  data_type: DataType = "receivable";
  char_id = ""; // id of char who is receiving it
  campaign_id = "";
  user_id = "";
  from_id = ""; // DM or id of char who sent it
  sent_dt = 0;
  received_dt = 0;
  message = "";
  temp = false;
  items: ReceivableItem[] = [];
  origin_id = ""; // This is used to keep the browser it was sent from from reacting to it.
  type: "Chat" | "Functional" | "Mail" = "Functional";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.campaign_id = obj.campaign_id ? obj.campaign_id : "";
      this.user_id = obj.user_id ? obj.user_id : "";
      this.from_id = obj.from_id ? obj.from_id : "";
      this.sent_dt = obj.sent_dt ? obj.sent_dt : 0;
      this.received_dt = obj.received_dt ? obj.received_dt : 0;
      this.message = obj.message ? obj.message : "";
      this.temp = obj.temp ? obj.temp : false;
      this.items = obj.items ? obj.items.map((item: any) => new ReceivableItem(item)) : [];
      this.origin_id = obj.origin_id ? obj.origin_id : "";
      this.type = obj.type ? obj.type : "Functional";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      char_id: this.char_id,
      campaign_id: this.campaign_id,
      user_id: this.user_id,
      from_id: this.from_id,  
      sent_dt: this.sent_dt,
      received_dt: this.received_dt,
      message: this.message,
      temp: this.temp,
      items: this.items.map((item: ReceivableItem) => item.toDBObj()),
      origin_id: this.origin_id,
      type: this.type,
    };
  }

  clone(): Receivable {
    const clone = new Receivable();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: Receivable): void {
    super.copy(copyMe);
    this.char_id = copyMe.char_id;
    this.campaign_id = copyMe.campaign_id;
    this.user_id = copyMe.user_id;
    this.from_id = copyMe.from_id;
    this.sent_dt = copyMe.sent_dt;
    this.received_dt = copyMe.received_dt;
    this.message = copyMe.message;
    this.items = copyMe.items.map((item: ReceivableItem) => item.clone());
    this.origin_id = copyMe.origin_id;
    this.type = copyMe.type;
  }
}

export type ReceivableObjectType = CharacterItem | CharacterCondition | CharacterGift | CharacterMagicItemKeyword;

export const isReceivableObject = (obj: any): obj is ReceivableObjectType => {
  return obj instanceof CharacterItem ||
    obj instanceof CharacterCondition ||
    obj instanceof CharacterGift ||
    obj instanceof CharacterMagicItemKeyword;
}

/**
 * Changing this so it can take more details about the things being sent. 
 * For Item it will send CharacterItem, for Condition it will send CharacterCondition, etc.
 * 
 */
export class ReceivableItem {
  type: "Condition" | "Item" | "Gift" | "Item Enhancement" = "Item";
  obj?: ReceivableObjectType;

  constructor(obj?: any) {
    if (obj) {
      this.type = obj.type ? obj.type : "Item";
      if (obj.obj) {
        switch (this.type) {
          case "Condition": this.obj = new CharacterCondition(obj.obj); break;
          case "Gift": this.obj = new CharacterGift(obj.obj); break;
          case "Item": this.obj = new CharacterItem(obj.obj); break;
          case "Item Enhancement": this.obj = new CharacterMagicItemKeyword(obj.obj); break;
        }
      }
    }
  }

  toDBObj(): any {
    return {
      type: this.type,
      obj: this.obj ? this.obj.toDBObj() : undefined
    };
  }

  clone(): ReceivableItem {
    const clone = new ReceivableItem();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: ReceivableItem): void {
    this.type = copyMe.type;
    this.obj = copyMe.obj ? copyMe.obj.clone() : undefined;
  }
}

export class Conversation {
  type: "Campaign" | "User" = "User";
  id = ""; // This is the id of the Campaign or User that they're chatting with.
  messages: Receivable[] = [];
  name = "";
  flashing = false;
  open = false;

  clone(): Conversation {
    const clone = new Conversation();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: Conversation): void {
    this.type = copyMe.type;
    this.id = copyMe.id;
    this.messages = [...copyMe.messages];
    this.name = copyMe.name;
    this.flashing = copyMe.flashing;
    this.open = copyMe.open;
  }
}